/**
 * Canonical placement context implementation.
 *
 * This is the source of truth for placement context consumers.
 * Consumers should import via:
 *   import { PlacementProvider, usePlacementContext } from '@atlassian/post-office-context/placement-context';
 */
import React, { type Context, createContext, useContext, useMemo } from 'react';

import { PostOfficeAnalyticsContext } from '@atlaskit/analytics-namespaced-context/PostOfficeAnalyticsContext';

import { usePostOfficeScreenPlacement } from '../post-office-screen-context';

// oxlint-disable-next-line eslint/no-redeclare
export type PlacementContext = {
	placementId?: string;
};

export const PlacementContext: Context<PlacementContext> = createContext<PlacementContext>({
	placementId: undefined,
});

export const PlacementProvider = ({
	value,
	children,
}: React.PropsWithChildren<{ value: PlacementContext }>): JSX.Element => {
	const memoizedValue = useMemo(
		() => ({
			placementId: value.placementId,
		}),
		[value.placementId],
	);

	usePostOfficeScreenPlacement(memoizedValue);

	return (
		<PlacementContext.Provider value={memoizedValue}>
			<PostOfficeAnalyticsContext data={memoizedValue}>{children}</PostOfficeAnalyticsContext>
		</PlacementContext.Provider>
	);
};

export const usePlacementContext = (): PlacementContext => useContext(PlacementContext);
